import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Gift, Heart, Coins, TrendingDown, LogOut, Settings as SettingsIcon, Users, Map, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import useAuth from "@/hooks/useAuth";
import { useGiftStats } from "@/hooks/useGiftStats";
import { useReceivedGifts } from "@/hooks/useReceivedGifts";
import { useSentGifts } from "@/hooks/useSentGifts";
import { useGiftNotifications } from "@/hooks/useGiftNotifications";
import { useSavedUsers } from "@/hooks/useSavedUsers";
import { formatCurrency } from "@/lib/formatters";
import StatsCard from "@/components/StatsCard";
import GiftCard from "@/components/GiftCard";
import GiftFilters from "@/components/GiftFilters";
import ExportButton from "@/components/ExportButton";
import EarningsBreakdown from "@/components/EarningsBreakdown";
import PayoutSetup from "@/components/PayoutSetup";
import Spinner from "@/components/Spinner";

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut, user } = useAuth();
  
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Get initial tab from URL query parameter
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'received';
  });
  
  // Enable real-time gift notifications
  useGiftNotifications();

  const { data: stats, isLoading: statsLoading } = useGiftStats(user?.id);
  const { data: receivedGifts = [], isLoading: receivedLoading } = useReceivedGifts(user?.id);
  const { data: sentGifts = [], isLoading: sentLoading } = useSentGifts(user?.id);
  const { data: savedUsers = [] } = useSavedUsers();
  
  // Fetch user profile for avatar
  const { data: profile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('users')
        .select('avatar_url, name')
        .eq('id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  // Check if user is an ambassador
  const isAmbassador = profile?.name?.toLowerCase().startsWith('ambassador');

  // Fetch prize draw stats for ambassador users
  const { data: prizeStats } = useQuery({
    queryKey: ['ambassador-prize-stats', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('prize_draw_entries')
        .select('entries, ambassador_share')
        .eq('ambassador_id', user.id)
        .eq('status', 'succeeded');
      
      if (error) throw error;
      
      const totalEntries = data?.reduce((sum, e) => sum + (e.entries || 0), 0) || 0;
      const totalCommission = data?.reduce((sum, e) => sum + (e.ambassador_share || 0), 0) || 0;
      
      return {
        totalPrizeEntries: totalEntries,
        prizeCommission: totalCommission / 100  // Convert pence to pounds
      };
    },
    enabled: !!user?.id && isAmbassador,
  });

  const handleLogout = async () => {
    await signOut();
    toast({
      title: "See you soon!",
      description: "You've been logged out.",
    });
    navigate("/");
  };

  // Filter received gifts
  const filteredReceivedGifts = useMemo(() => {
    return receivedGifts.filter(gift => {
      const matchesStatus = statusFilter === "all" || gift.status === statusFilter;
      const matchesSearch = !searchTerm || 
        gift.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (!gift.is_anonymous && gift.sender?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesStatus && matchesSearch;
    });
  }, [receivedGifts, statusFilter, searchTerm]);

  // Filter sent gifts
  const filteredSentGifts = useMemo(() => {
    return sentGifts.filter(gift => {
      const matchesStatus = statusFilter === "all" || gift.status === statusFilter;
      const matchesSearch = !searchTerm || 
        gift.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        gift.recipient?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [sentGifts, statusFilter, searchTerm]);

  // Prepare export data for received gifts
  const receivedExportData = filteredReceivedGifts.map(gift => ({
    date: gift.created_at,
    type: "Received",
    user: gift.is_anonymous ? "Anonymous" : gift.sender?.name || "Unknown",
    amount: gift.amount,
    currency: gift.currency,
    status: gift.status,
    purpose: gift.purpose || "Not specified",
    message: gift.message,
  }));

  // Prepare export data for sent gifts
  const sentExportData = filteredSentGifts.map(gift => ({
    date: gift.created_at,
    type: "Sent",
    user: gift.recipient?.name || "Unknown",
    amount: gift.amount,
    currency: gift.currency,
    status: gift.status,
    purpose: gift.purpose || "Not specified",
    message: gift.message,
  }));

  if (statsLoading || receivedLoading || sentLoading) {
    return <Spinner />;
  }
  console.log('Dashboard render:', { user, receivedGifts, sentGifts });

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 pb-20 overflow-x-hidden">
      <div className="container mx-auto px-4 pt-safe pb-6 max-w-md md:max-w-2xl lg:max-w-4xl overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12 border-2 border-primary/20 shadow-sm">
              <AvatarImage src={profile?.avatar_url || undefined} alt="Profile" />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {profile?.name?.slice(0, 2).toUpperCase() || user?.email?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl font-bold">Dashboard</h1>
              <p className="text-sm text-muted-foreground">Track your gifts</p>
            </div>
          </div>
          <div className="flex items-center gap-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/map")}
              className="h-10 w-10"
            >
              <Map className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings")}
              className="h-10 w-10"
            >
              <SettingsIcon className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="h-10 w-10"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <StatsCard
            icon={Gift}
            label="Gifts Sent"
            value={stats?.totalSent || 0}
            iconColor="text-primary"
          />
          <StatsCard
            icon={Heart}
            label="Gifts Received"
            value={stats?.totalReceived || 0}
            iconColor="text-secondary"
          />
          <StatsCard
            icon={Coins}
            label="Total Earnings"
            value={formatCurrency(stats?.totalEarnings || 0)}
            iconColor="text-green-600"
          />
          <div 
            onClick={() => navigate("/saved-users")}
            className="cursor-pointer"
          >
            <StatsCard
              icon={Users}
              label="Saved Users"
              value={savedUsers.length}
              iconColor="text-blue-600"
            />
          </div>
          
          {/* Ambassador-only stats */}
          {isAmbassador && prizeStats && (
            <>
              <StatsCard
                icon={Ticket}
                label="Prize Entries"
                value={prizeStats.totalPrizeEntries}
                iconColor="text-purple-600"
              />
              <StatsCard
                icon={Coins}
                label="Prize Commission"
                value={`£${prizeStats.prizeCommission.toFixed(2)}`}
                iconColor="text-amber-600"
              />
            </>
          )}
        </div>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="received">Received</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
          </TabsList>

          {/* Received Gifts Tab */}
          <TabsContent value="received" className="space-y-4">
            <GiftFilters
              statusFilter={statusFilter}
              searchTerm={searchTerm}
              onStatusFilterChange={setStatusFilter}
              onSearchChange={setSearchTerm}
            />
            
            <ExportButton data={receivedExportData} filename="received-gifts" />

            <div className="space-y-3">
              {filteredReceivedGifts.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No gifts received yet</p>
                </div>
              ) : (
                filteredReceivedGifts.map((gift) => (
                  <GiftCard
                    key={gift.id}
                    type="received"
                    amount={gift.amount}
                    netAmount={gift.net_amount}
                    currency={gift.currency}
                    message={gift.message}
                    status={gift.status}
                    created_at={gift.created_at}
                    isAnonymous={gift.is_anonymous}
                    userName={gift.sender?.name}
                    userAvatar={gift.sender?.avatar_url}
                    purpose={gift.purpose || undefined}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Sent Gifts Tab */}
          <TabsContent value="sent" className="space-y-4">
            <GiftFilters
              statusFilter={statusFilter}
              searchTerm={searchTerm}
              onStatusFilterChange={setStatusFilter}
              onSearchChange={setSearchTerm}
            />
            
            <ExportButton data={sentExportData} filename="sent-gifts" />

            <div className="space-y-3">
              {filteredSentGifts.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No gifts sent yet</p>
                </div>
              ) : (
                filteredSentGifts.map((gift) => (
                  <GiftCard
                    key={gift.id}
                    type="sent"
                    amount={gift.amount}
                    commissionAmount={gift.commission_amount}
                    currency={gift.currency}
                    message={gift.message}
                    status={gift.status}
                    created_at={gift.created_at}
                    isAnonymous={gift.is_anonymous}
                    userName={gift.recipient?.name}
                    userAvatar={gift.recipient?.avatar_url}
                    purpose={gift.purpose || undefined}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings" className="space-y-4">
            <PayoutSetup />
            <EarningsBreakdown gifts={receivedGifts} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;